// Arrears KPI tile (task 4.1 of the dashboard-redesign spec — Req 5.1, 5.2, 5.5).
//
// A read-only widget summarising the tenant's total outstanding arrears
// across four aging buckets (0–30, 31–60, 61–90, 90+ days past due). The
// numbers come from the `reports` spec's aged-debtors endpoint, which itself
// reconciles to the GL Debtors Control account per Constitution Rule 2 —
// this widget does NOT re-aggregate financial records.
//
// State model (see design.md §Widget state contracts):
//   - loading → <WidgetSkeleton>       (bounding box stable)
//   - empty   → "No arrears" muted     (Req 5.5)
//   - ready   → total + 4-bucket pips + deep-link (Req 5.1, 5.2)
//   - error   → <WidgetError> with ErrorId + Retry (Req 14)
//
// The entire subtree is wrapped in <ErrorBoundary> so a render exception
// inside this widget cannot blank the surrounding dashboard (P4 in
// design.md §Correctness Properties).

import React from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";
import { WidgetFrame } from "../WidgetFrame";
import { WidgetError } from "../WidgetError";
import { useDashboardArrears } from "../hooks/useDashboardArrears";
import { formatMoneyGBP, getMonetaryColour } from "../formatMoney";
import type { ArrearsAging, BuildingScope } from "../types";

/** Widget props — every dashboard widget takes exactly `scope`. */
export interface WidgetArrearsProps {
  scope: BuildingScope;
}

/**
 * Static ordered mapping from `ArrearsAging.buckets` keys to their display
 * labels. Ordered oldest-first as it appears in the design-mockup pill row.
 */
const BUCKET_LABELS: ReadonlyArray<{
  key: keyof ArrearsAging["buckets"];
  label: string;
}> = [
  { key: "days0to30", label: "0–30" },
  { key: "days31to60", label: "31–60" },
  { key: "days61to90", label: "61–90" },
  { key: "days90plus", label: "90+" },
];

/**
 * Build the deep-link `to` prop for `<Link>`. Aggregate mode links to the
 * report unfiltered; single-building mode carries the `?buildingId=` query
 * so the report page pre-filters to the same building.
 */
function buildAgedDebtorsHref(scope: BuildingScope): string {
  if (scope.kind === "single") {
    return `/dashboard/reports/aged-debtors?buildingId=${encodeURIComponent(
      scope.buildingId,
    )}`;
  }
  return "/dashboard/reports/aged-debtors";
}

/**
 * Widget_Arrears — a KPI tile summarising the tenant's total outstanding
 * arrears, broken into four aging buckets, with a deep-link to the aged
 * debtors report.
 *
 * @remarks
 * All monetary values are integer pence (Constitution Rule 1). They render
 * via `formatMoneyGBP` and are coloured via `getMonetaryColour(_, "owed")`
 * because arrears are a receivable — a positive balance is money owed **to**
 * the tenant (green) and a negative balance would flip to red.
 */
export function Widget_Arrears({ scope }: WidgetArrearsProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={
        <WidgetError errorId="widget-arrears-render-error" name="Arrears" />
      }
    >
      <WidgetArrearsInner scope={scope} />
    </ErrorBoundary>
  );
}

function WidgetArrearsInner({ scope }: WidgetArrearsProps): JSX.Element {
  const result = useDashboardArrears(scope);
  const href = buildAgedDebtorsHref(scope);

  return (
    <WidgetFrame
      result={result}
      name="Arrears"
      emptyCopy="No arrears"
      skeletonHeight={140}
    >
      {result.status === "ready" && (
        <div
          data-testid="widget-arrears"
          className="w-full h-full flex flex-col justify-center gap-2 px-4 py-3"
        >
          <div
            className="text-xs uppercase tracking-wide"
            style={{ color: "#6b7280", fontWeight: 500 }}
          >
            Arrears · Aged Debtors
          </div>

          <div
            data-testid="widget-arrears-total"
            className="text-3xl font-bold"
            style={{
              color: getMonetaryColour(result.data.totalPence, "owed"),
            }}
          >
            {formatMoneyGBP(result.data.totalPence)}
          </div>

          <div
            className="flex flex-wrap gap-2"
            data-testid="widget-arrears-buckets"
          >
            {BUCKET_LABELS.map(({ key, label }) => {
              const pence = result.data.buckets[key];
              return (
                <span
                  key={key}
                  data-testid={`widget-arrears-bucket-${key}`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                  style={{ backgroundColor: "#f3f4f6", color: "#374151" }}
                >
                  <span className="font-medium">{label}</span>
                  <span style={{ color: getMonetaryColour(pence, "owed") }}>
                    {formatMoneyGBP(pence)}
                  </span>
                </span>
              );
            })}
          </div>

          <Link
            to={href}
            data-testid="widget-arrears-link"
            className="text-xs hover:underline"
            style={{ color: "#2563eb" }}
          >
            View aged debtors →
          </Link>
        </div>
      )}
    </WidgetFrame>
  );
}
